import { useEffect, useState, type FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { CheckCircle2, Eye, EyeOff, Loader2, Lock, ShieldCheck, XCircle } from 'lucide-react'
import { config } from '@/config/runtime-config'
import { api } from '@/shared/api/client'
import { ApiError, explain } from '@/shared/api/problem'
import { fmtDateTime } from '@/shared/lib/format'
import type { InvitationStatusResponse } from '@/shared/api/types'

const MIN_LENGTH = 12

/**
 * Where an invitation link lands. Anonymous by design — the invitee has no
 * session yet, which is the entire reason the flow exists.
 *
 * The token is validated on load rather than on submit, so an expired link says
 * so on arrival instead of after someone has typed a password twice.
 *
 * Visual language is the login page's right-hand column: same card, same h-11
 * fields, same chocolate submit button.
 */
export function AcceptInvitationPage() {
  const { token = '' } = useParams()
  const navigate = useNavigate()

  const [status, setStatus] = useState<InvitationStatusResponse | null>(null)
  const [checking, setChecking] = useState(true)
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [show, setShow] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)

  useEffect(() => { document.title = `Accept invitation — ${config.appName}` }, [])

  useEffect(() => {
    let cancelled = false
    setChecking(true)
    api.get<InvitationStatusResponse>(`/auth/invitation/${encodeURIComponent(token)}`,
      { anonymous: true })
      .then((s) => { if (!cancelled) setStatus(s) })
      .catch(() => {
        if (!cancelled) setStatus({ valid: false, reason: 'NOT_FOUND' })
      })
      .finally(() => { if (!cancelled) setChecking(false) })
    return () => { cancelled = true }
  }, [token])

  const tooShort = password.length > 0 && password.length < MIN_LENGTH
  const mismatch = confirm.length > 0 && password !== confirm
  const valid = password.length >= MIN_LENGTH && password === confirm

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (!valid) return
    setBusy(true)
    setError('')
    try {
      await api.post('/auth/invitation/accept', { token, newPassword: password },
        { anonymous: true })
      setDone(true)
      setTimeout(() => navigate('/login', { replace: true }), 2500)
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setError('That link is no longer valid. Ask an administrator to send a new one.')
      } else {
        setError(explain(err))
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-background">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-11 w-11 rounded-xl bg-chocolate text-chocolate-foreground inline-flex items-center justify-center font-mono text-xl font-bold shadow">
            {config.brandMark}
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight">{config.appName}</h1>
            <p className="text-erp-xs text-muted-foreground -mt-0.5">{config.appTagline}</p>
          </div>
        </div>

        {checking ? (
          <div className="erp-card px-4 py-10 text-center text-erp-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Checking your invitation…
          </div>
        ) : done ? (
          <div className="erp-card px-4 py-8 text-center">
            <CheckCircle2 className="h-8 w-8 text-emerald-600 mx-auto mb-3" />
            <h2 className="text-erp-lg font-semibold">Password set</h2>
            <p className="text-erp-sm text-muted-foreground mt-1">
              Taking you to the sign-in page…
            </p>
          </div>
        ) : !status?.valid ? (
          <div className="erp-card px-4 py-8 text-center">
            <XCircle className="h-8 w-8 text-red-600 mx-auto mb-3" />
            <h2 className="text-erp-lg font-semibold">{reasonTitle(status?.reason)}</h2>
            <p className="text-erp-sm text-muted-foreground mt-1 max-w-sm mx-auto">
              {reasonHint(status?.reason)}
            </p>
            <a href={`mailto:${config.login.supportEmail}`} className="erp-link text-erp-sm mt-4 inline-block">
              Contact {config.login.supportEmail}
            </a>
          </div>
        ) : (
          <>
            <div className="mb-5">
              <h2 className="text-2xl font-bold tracking-tight">Set your password</h2>
              <p className="text-erp-sm text-muted-foreground mt-1">
                {status.email
                  ? <>Finish setting up <span className="font-medium">{status.email}</span>.</>
                  : 'Choose a password to finish setting up your account.'}
                {status.expiresAt && <> This link expires {fmtDateTime(status.expiresAt)}.</>}
              </p>
            </div>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <label htmlFor="pw" className="text-erp-xs font-medium text-muted-foreground mb-1.5 block">
                  New password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/70 pointer-events-none" />
                  <input
                    id="pw"
                    type={show ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                    className="w-full h-11 pl-10 pr-10 text-erp-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring transition-colors"
                  />
                  <button
                    type="button"
                    onClick={() => setShow((s) => !s)}
                    tabIndex={-1}
                    aria-label={show ? 'Hide password' : 'Show password'}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1 rounded"
                  >
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className={`text-erp-xs mt-1 ${tooShort ? 'text-red-700' : 'text-muted-foreground'}`}>
                  At least {MIN_LENGTH} characters.
                </p>
              </div>

              <div>
                <label htmlFor="pw2" className="text-erp-xs font-medium text-muted-foreground mb-1.5 block">
                  Confirm password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/70 pointer-events-none" />
                  <input
                    id="pw2"
                    type={show ? 'text' : 'password'}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    autoComplete="new-password"
                    required
                    className="w-full h-11 pl-10 pr-3 text-erp-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring transition-colors"
                  />
                </div>
                {mismatch && <p className="text-erp-xs text-red-700 mt-1">The two passwords do not match.</p>}
              </div>

              {error && (
                <div className="p-2.5 bg-rose-50 border border-rose-200 rounded-md text-erp-sm text-rose-700 flex items-start gap-2">
                  <span className="text-rose-500 mt-0.5">⚠</span>
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={!valid || busy}
                className="w-full h-11 inline-flex items-center justify-center text-erp-sm bg-chocolate text-chocolate-foreground rounded-md hover:bg-chocolate-muted disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors shadow-sm"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
                Set password and continue
              </button>
            </form>
          </>
        )}

        <p className="mt-6 text-erp-xs text-muted-foreground text-center">
          {config.appName} · {config.release} · {config.environment}
        </p>
      </div>
    </div>
  )
}

function reasonTitle(reason?: string): string {
  switch (reason) {
    case 'EXPIRED': return 'This invitation has expired'
    case 'CONSUMED': return 'This invitation has already been used'
    case 'REVOKED': return 'This invitation was revoked'
    default: return 'This invitation link is not valid'
  }
}

function reasonHint(reason?: string): string {
  switch (reason) {
    case 'EXPIRED':
      return 'Invitations are time-limited. An administrator can issue a fresh one from your user record.'
    case 'CONSUMED':
      return 'The password has already been set. Sign in instead, or ask for a password reset.'
    case 'REVOKED':
      return 'A newer invitation may have replaced it — check for a more recent email.'
    default:
      return 'Check that you copied the whole link, including everything after the last slash.'
  }
}
